/**
 * Responsive tables — one system for every <table> in the app.
 *
 * On phones a data table becomes a stack of cards: each <tr> is a
 * card, each <td> a "label: value" line, using the column's <th> text
 * as the label. The CSS (app/responsive-tables.prefix.css) renders
 * that from ``data-label`` on the cells; this module STAMPS those
 * labels automatically so no page has to hand-annotate 17 tables (and
 * future tables get it for free).
 *
 * Mechanics: walk each <table>'s header row, collect th texts, write
 * ``data-label`` on every body cell whose column has a header. Cells
 * that already carry data-label are left alone. Runs once on load and
 * again on DOM mutations (React re-renders / pagination), throttled
 * to one pass per animation frame.
 *
 * Opt out per table with ``data-no-cards`` (e.g. a genuinely wide
 * spreadsheet-like grid that must stay a grid); it then keeps the
 * legacy horizontal-scroll behaviour.
 */

const CELL_TAG = /^(TD|TH)$/;

const headerTexts = (table: HTMLTableElement): string[] => {
  const headRow =
    table.tHead?.rows?.[0] ??
    // Tables without <thead>: first row made only of <th> counts.
    Array.from(table.rows).find((r) => r.cells.length && Array.from(r.cells).every((c) => c.tagName === "TH"));
  if (!headRow) return [];
  const out: string[] = [];
  Array.from(headRow.cells).forEach((c) => {
    const span = Math.max(1, Number(c.getAttribute("colspan") || 1));
    const text = (c.textContent || "").replace(/\s+/g, " ").trim();
    for (let i = 0; i < span; i += 1) out.push(text);
  });
  return out;
};

const stampTable = (table: HTMLTableElement): void => {
  if (table.hasAttribute("data-no-cards")) return;
  const labels = headerTexts(table);
  if (!labels.length) return;
  const bodyRows = table.tBodies.length
    ? Array.from(table.tBodies).flatMap((b) => Array.from(b.rows))
    : Array.from(table.rows).filter((r) => !Array.from(r.cells).every((c) => c.tagName === "TH"));
  for (const row of bodyRows) {
    let col = 0;
    for (const cell of Array.from(row.cells)) {
      if (!CELL_TAG.test(cell.tagName)) continue;
      const label = labels[col];
      if (label && !cell.hasAttribute("data-label")) cell.setAttribute("data-label", label);
      // Blank-header columns (action buttons) get flagged so the CSS
      // can render them as a trailing action row without a label.
      if (!label && !cell.hasAttribute("data-label")) cell.setAttribute("data-unlabeled", "");
      col += Math.max(1, Number(cell.getAttribute("colspan") || 1));
    }
  }
  table.setAttribute("data-cards-ready", "");
};

let scheduled = false;
const stampAll = (): void => {
  scheduled = false;
  document.querySelectorAll<HTMLTableElement>("table").forEach(stampTable);
};

const schedule = (): void => {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(stampAll);
};

export const installResponsiveTables = (): void => {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if ((window as { __afri_rt_installed?: boolean }).__afri_rt_installed) return;
  (window as { __afri_rt_installed?: boolean }).__afri_rt_installed = true;
  const start = () => {
    schedule();
    const mo = new MutationObserver(schedule);
    mo.observe(document.body, { childList: true, subtree: true });
  };
  if (document.body) start();
  else document.addEventListener("DOMContentLoaded", start, { once: true });
};
